import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControl, Grid, IconButton, InputLabel,
  LinearProgress, MenuItem, Paper, Select, Snackbar, Stack, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import apiClient from '../../services/apiClient';
import AnnotatedStoryText from '../../components/AnnotatedStoryText';
import { getLanguageDisplayName, SUPPORTED_LANGUAGES } from '../../config/languagesConfig';
import { cloneAnnotations, gapWords, layoutSentence } from '../../utils/wordMatching';

/**
 * Word-matching workbench (admin). For one chapter + language:
 *  - each sentence side by side with its English (editable),
 *  - tappable units shown as blocks; click one to edit its meaning / dictionary
 *    form, join it with the next word into a phrase, split a phrase, or remove it,
 *  - untappable words highlighted in red; click one to make it tappable,
 *  - build with AI (chunk by chunk, resumable), rebuild one paragraph,
 *  - the chapter's section (part) name, and a learner preview.
 * Server format: backend/services/story_annotations.py.
 */

const STATUS = {
  done: { label: 'Done', color: 'success' },
  draft: { label: 'Partly built', color: 'warning' },
  stale: { label: 'Out of date', color: 'error' },
  none: { label: 'Not built', color: 'default' },
};
const langLabel = (code) => `${SUPPORTED_LANGUAGES?.[code]?.flag || ''} ${getLanguageDisplayName?.(code) || code}`.trim();
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Live counts from the working copy (the server re-checks on save).
function draftStats(draft) {
  const s = { sentences: 0, units: 0, phrases: 0, untappable: 0, missingEnglish: 0, emptyMeanings: 0, words: 0 };
  (draft?.paragraphs || []).forEach((para) => para.forEach((sent) => {
    s.sentences += 1;
    if (!sent.en) s.missingEnglish += 1;
    const units = sent.w || [];
    s.units += units.length;
    s.phrases += units.filter((u) => /\s/.test(u.t)).length;
    s.emptyMeanings += units.filter((u) => !u.g).length;
    layoutSentence(sent.text, units).forEach((piece) => {
      if (piece.type === 'gap' && piece.hasWords) s.untappable += gapWords(piece.text).length;
    });
    s.words += sent.text.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
  }));
  s.coverage = s.words ? Math.max(0, Math.round(((s.words - s.untappable) / s.words) * 100)) : 0;
  return s;
}

export default function WordMatchingWorkbench() {
  const { storyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [story, setStory] = useState(null);
  const [lang, setLang] = useState(searchParams.get('lang') || '');
  const [data, setData] = useState(null);           // GET word-matching response
  const [draft, setDraft] = useState(null);         // working copy of annotations
  const [dirty, setDirty] = useState(false);
  const [selected, setSelected] = useState(null);   // { p, s, i }
  const [view, setView] = useState('edit');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [build, setBuild] = useState(null);         // { label, done, total }
  const [serverReport, setServerReport] = useState(null);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');
  const [section, setSection] = useState('');

  // Story + its languages
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.admin.getStory(storyId);
        const s = res.data.story;
        setStory(s);
        setSection(s.section || '');
        if (!lang) {
          const langs = (s.translations || []).filter((t) => (t.translation || '').trim()).map((t) => t.language_code);
          setLang(langs.find((l) => l !== 'en') || langs[0] || 'en');
        }
      } catch (e) {
        setError('Could not load this story.');
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  const load = useCallback(async () => {
    if (!lang) return;
    setLoading(true);
    setError('');
    setSelected(null);
    try {
      const res = await apiClient.admin.getWordMatching(storyId, lang);
      setData(res.data);
      setServerReport(res.data.report);
      const usable = res.data.annotations && res.data.word_matching !== 'stale';
      setDraft(usable ? cloneAnnotations(res.data.annotations) : null);
      setDirty(false);
    } catch (e) {
      setData(null);
      setDraft(null);
      setError(e?.response?.status === 404
        ? `There's no ${langLabel(lang)} translation for this chapter yet.`
        : 'Could not load word matching.');
    } finally {
      setLoading(false);
    }
  }, [storyId, lang]);

  useEffect(() => { load(); }, [load]);

  const changeLang = (value) => {
    if (dirty) { setSnack('Save or discard your changes first.'); return; }
    setLang(value);
    setSearchParams({ lang: value });
  };

  // ---- editing helpers (all on the working copy) ----
  const edit = (fn) => {
    setDraft((prev) => { const next = cloneAnnotations(prev); fn(next); return next; });
    setDirty(true);
  };
  const unitAt = (d, { p, s, i }) => d.paragraphs[p][s].w[i];
  const setUnitField = (field, value) => edit((d) => {
    const u = unitAt(d, selected);
    if (field === 'l' && !value) delete u.l; else u[field] = value;
  });
  const setEnglish = (p, s, value) => edit((d) => { d.paragraphs[p][s].en = value; });
  const joinWithNext = () => edit((d) => {
    const sent = d.paragraphs[selected.p][selected.s];
    const pieces = layoutSentence(sent.text, sent.w).filter((x) => x.type === 'unit');
    const a = pieces.find((x) => x.index === selected.i);
    const b = pieces.find((x) => x.index === selected.i + 1);
    if (!a || !b) return;
    const merged = { t: sent.text.slice(a.start, b.end), g: [sent.w[a.index].g, sent.w[b.index].g].filter(Boolean).join(' ') };
    sent.w.splice(selected.i, 2, merged);
  });
  const splitPhrase = () => edit((d) => {
    const sent = d.paragraphs[selected.p][selected.s];
    const words = sent.w[selected.i].t.split(/\s+/).filter(Boolean);
    sent.w.splice(selected.i, 1, ...words.map((t) => ({ t, g: '' })));
  });
  const removeUnit = () => { edit((d) => { d.paragraphs[selected.p][selected.s].w.splice(selected.i, 1); }); setSelected(null); };
  const addUnit = (p, s, insertAt, word) => {
    edit((d) => {
      const sent = d.paragraphs[p][s];
      sent.w = sent.w || [];
      sent.w.splice(insertAt, 0, { t: word, g: '' });
    });
    setSelected({ p, s, i: insertAt });
  };

  // ---- server actions ----
  const save = async () => {
    setSaving(true);
    try {
      const res = await apiClient.admin.saveWordMatching(storyId, lang, draft);
      setServerReport(res.data.report);
      setSnack('Word matching saved.');
      await load();
    } catch (e) {
      const rep = e?.response?.data?.report;
      if (rep) setServerReport(rep);
      setSnack(e?.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // Build chunk by chunk (each request stays under Heroku's 30s limit).
  const runBuild = async ({ reset = false, chunks = null, label }) => {
    setBuild({ label, done: 0, total: chunks ? chunks.length : 1 });
    let failures = 0;
    try {
      if (chunks) {
        for (let k = 0; k < chunks.length; k++) {
          try {
            await apiClient.admin.buildWordMatching(storyId, lang, { chunk: chunks[k] });
            failures = 0;
          } catch (e) {
            if (++failures > 2) throw e;
            await sleep(4000 * failures); k -= 1; continue;
          }
          setBuild({ label, done: k + 1, total: chunks.length });
        }
      } else {
        let first = true;
        for (;;) {
          let res;
          try {
            res = await apiClient.admin.buildWordMatching(storyId, lang, { reset: reset && first });
            first = false;
            failures = 0;
          } catch (e) {
            if (++failures > 2) throw e;
            await sleep(4000 * failures); continue;
          }
          const total = res.data.chunk_count || 1;
          setBuild({ label, done: total - (res.data.remaining || 0), total });
          if (res.data.done) break;
        }
      }
      setSnack(`${label}: done.`);
    } catch (e) {
      setSnack(`${label} stopped: ${e?.response?.data?.error || e.message}. Run it again to continue.`);
    } finally {
      setBuild(null);
      await load();
    }
  };

  const saveSection = async () => {
    try {
      await apiClient.admin.updateStory(storyId, { section });
      setStory((s) => ({ ...s, section: section.trim() || null }));
      setSnack('Section saved.');
    } catch (e) {
      setSnack('Could not save the section.');
    }
  };

  const stats = useMemo(() => draftStats(draft), [draft]);
  const languages = useMemo(
    () => (story?.translations || []).filter((t) => (t.translation || t.content || '').trim()).map((t) => t.language_code),
    [story]
  );

  const status = data?.word_matching || 'none';
  const chunkList = serverReport?.chunks || [];
  const busy = Boolean(build) || saving;
  const skeleton = data?.skeleton;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <IconButton aria-label="Back" onClick={() => navigate(story?.path_id ? `/admin/paths/${story.path_id}/matrix` : '/admin/paths')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1, minWidth: 220 }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Word matching</Typography>
          <Typography variant="body2" color="text.secondary">
            {story?.title}{data?.translated_title && data.translated_title !== story?.title ? ` · ${data.translated_title}` : ''}
            {story?.path?.title ? ` · ${story.path.title}` : ''}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Language</InputLabel>
          <Select label="Language" value={languages.includes(lang) ? lang : ''} onChange={(e) => changeLang(e.target.value)}>
            {languages.map((l) => <MenuItem key={l} value={l}>{langLabel(l)}</MenuItem>)}
          </Select>
        </FormControl>
        <Chip label={STATUS[status]?.label || status} color={STATUS[status]?.color || 'default'} variant="outlined" />
        {status !== 'done' && (
          <Tooltip title={dirty ? 'Save or discard your changes first' : ''}>
            <span>
              <Button
                variant="outlined" startIcon={<AutoFixHighIcon />} disabled={busy || dirty || !data}
                onClick={() => runBuild({ label: status === 'draft' ? 'Finish building' : 'Build with AI', reset: status === 'stale' })}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {status === 'draft' ? 'Finish building' : 'Build with AI'}
              </Button>
            </span>
          </Tooltip>
        )}
        {status === 'done' && (
          <Tooltip title={dirty ? 'Save or discard your changes first' : 'Rebuild everything from scratch (replaces your edits)'}>
            <span>
              <Button variant="text" startIcon={<RefreshIcon />} disabled={busy || dirty}
                onClick={() => runBuild({ label: 'Rebuild all', reset: true })} sx={{ textTransform: 'none' }}>
                Rebuild all
              </Button>
            </span>
          </Tooltip>
        )}
        <Button variant="text" startIcon={<UndoIcon />} disabled={!dirty || busy} onClick={load} sx={{ textTransform: 'none' }}>
          Discard
        </Button>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          disabled={!dirty || busy} onClick={save} sx={{ textTransform: 'none', borderRadius: 2 }}>
          Save
        </Button>
      </Stack>

      {build && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>{build.label} — {build.done}/{build.total} chunks</Typography>
          <LinearProgress variant="determinate" value={Math.round((build.done / Math.max(1, build.total)) * 100)} />
        </Paper>
      )}

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
      {status === 'stale' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          The {langLabel(lang)} text changed after this word matching was made, so learners see plain text. Build again to update it.
        </Alert>
      )}
      {serverReport?.errors?.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {serverReport.errors.map((e, i) => <div key={i}>{e}</div>)}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Coverage */}
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            {draft ? (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{stats.coverage}% of words tappable</Typography>
                  {dirty && <Typography variant="caption" color="warning.main">Unsaved changes</Typography>}
                </Stack>
                <LinearProgress variant="determinate" value={stats.coverage} color={stats.coverage === 100 ? 'success' : 'primary'}
                  sx={{ height: 8, borderRadius: 4, my: 1 }} />
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" variant="outlined" label={`${stats.sentences} sentences`} />
                  <Chip size="small" variant="outlined" label={`${stats.units} tappable units`} />
                  <Chip size="small" variant="outlined" label={`${stats.phrases} phrases`} />
                  <Chip size="small" variant="outlined" color={stats.untappable ? 'error' : 'default'} label={`${stats.untappable} untappable words`} />
                  <Chip size="small" variant="outlined" color={stats.missingEnglish ? 'warning' : 'default'} label={`${stats.missingEnglish} sentences without English`} />
                  <Chip size="small" variant="outlined" color={stats.emptyMeanings ? 'warning' : 'default'} label={`${stats.emptyMeanings} units without a meaning`} />
                </Stack>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No word matching yet{status === 'stale' ? ' for the current text' : ''}. Build it with AI, or have Claude author it
                through the MCP (<code>set_word_matching</code>), which is best for hand-written translations.
              </Typography>
            )}
          </Paper>
        </Grid>
        {/* Section */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Section</Typography>
            <Stack direction="row" spacing={1}>
              <TextField size="small" fullWidth placeholder="e.g. Part One: The Offer" value={section}
                onChange={(e) => setSection(e.target.value)} />
              <Button variant="outlined" onClick={saveSection} disabled={(story?.section || '') === section}
                sx={{ textTransform: 'none', flexShrink: 0 }}>
                Save
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Consecutive chapters with the same section are grouped under one heading on the learner's trail.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : draft ? (
        <>
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, v) => v && setView(v)} sx={{ mb: 2 }}>
            <ToggleButton value="edit" sx={{ textTransform: 'none', px: 2 }}>Edit</ToggleButton>
            <ToggleButton value="preview" sx={{ textTransform: 'none', px: 2 }}>Learner preview</ToggleButton>
          </ToggleButtonGroup>

          {view === 'preview' ? (
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 4 }, borderRadius: 2, maxWidth: 820 }}>
              <AnnotatedStoryText
                annotations={draft}
                languageCode={lang}
                typographySx={{ fontFamily: '"Georgia", "Times New Roman", serif', fontSize: '1.15rem', lineHeight: 1.95 }}
              />
            </Paper>
          ) : (
            <Stack spacing={2}>
              <Typography variant="caption" color="text.secondary">
                Click a unit to edit its meaning, join it into a phrase, or split it. Words in red can't be tapped yet; click one to add it.
              </Typography>
              {draft.paragraphs.map((para, p) => {
                const paraChunks = chunkList.map((c, idx) => (c[0] === p ? idx : null)).filter((x) => x !== null);
                return (
                  <Paper key={p} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, flexGrow: 1 }}>Paragraph {p + 1}</Typography>
                      {paraChunks.length > 0 && (
                        <Tooltip title={dirty ? 'Save or discard your changes first' : 'Rebuild this paragraph with AI (replaces its edits)'}>
                          <span>
                            <Button size="small" startIcon={<RefreshIcon />} disabled={busy || dirty}
                              onClick={() => runBuild({ chunks: paraChunks, label: `Rebuild paragraph ${p + 1}` })}
                              sx={{ textTransform: 'none' }}>
                              Rebuild paragraph
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                    {para.map((sent, s) => (
                      <SentenceRow
                        key={s}
                        sentence={sent}
                        p={p}
                        s={s}
                        selected={selected && selected.p === p && selected.s === s ? selected.i : null}
                        onSelect={(i) => setSelected(i === null ? null : { p, s, i })}
                        onAddUnit={addUnit}
                        onEnglish={(v) => setEnglish(p, s, v)}
                        editor={selected && selected.p === p && selected.s === s ? (
                          <UnitEditor
                            unit={sent.w[selected.i]}
                            canJoin={selected.i < (sent.w || []).length - 1}
                            onChange={setUnitField}
                            onJoin={joinWithNext}
                            onSplit={splitPhrase}
                            onRemove={removeUnit}
                            onClose={() => setSelected(null)}
                          />
                        ) : null}
                      />
                    ))}
                  </Paper>
                );
              })}
            </Stack>
          )}
        </>
      ) : skeleton ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {skeleton.reduce((n, para) => n + para.length, 0)} sentences in {skeleton.length} paragraphs
          </Typography>
          {skeleton.map((para, p) => (
            <Typography key={p} variant="body2" color="text.secondary" sx={{ mb: 1.5, fontFamily: 'Georgia, serif' }}>
              {para.map((x) => x.text).join(' ')}
            </Typography>
          ))}
        </Paper>
      ) : null}

      <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}

function SentenceRow({ sentence, p, s, selected, onSelect, onAddUnit, onEnglish, editor }) {
  const pieces = layoutSentence(sentence.text, sentence.w);
  let unitsSoFar = 0;
  return (
    <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Typography component="div" variant="body1" sx={{ fontFamily: '"Georgia", serif', lineHeight: 2.1 }}>
            {pieces.map((piece, k) => {
              if (piece.type === 'unit') {
                unitsSoFar = piece.index + 1;
                const isSel = selected === piece.index;
                const noMeaning = !piece.unit.g;
                return (
                  <Tooltip key={k} title={piece.unit.g ? `${piece.unit.g}${piece.unit.l ? ` · ${piece.unit.l}` : ''}` : 'No meaning yet'}>
                    <Box
                      component="span"
                      onClick={() => onSelect(isSel ? null : piece.index)}
                      sx={{
                        cursor: 'pointer', px: 0.4, py: 0.1, mx: 0.05, borderRadius: 1,
                        bgcolor: (t) => alpha(t.palette.primary.main, isSel ? 0.22 : 0.07),
                        border: '1px solid',
                        borderColor: isSel ? 'primary.main' : noMeaning ? 'warning.main' : 'transparent',
                        borderStyle: noMeaning && !isSel ? 'dashed' : 'solid',
                        '&:hover': { borderColor: 'primary.light' },
                      }}
                    >
                      {piece.text}
                    </Box>
                  </Tooltip>
                );
              }
              if (!piece.hasWords) return <span key={k}>{piece.text}</span>;
              // Gap containing words that aren't tappable: render them as red "add" targets.
              const insertAt = unitsSoFar;
              const words = gapWords(piece.text);
              const out = [];
              let last = 0;
              words.forEach(({ word, offset }, n) => {
                if (offset > last) out.push(<span key={`g${n}`}>{piece.text.slice(last, offset)}</span>);
                out.push(
                  <Tooltip key={`w${n}`} title="Not tappable — click to add">
                    <Box component="span" onClick={() => onAddUnit(p, s, insertAt, word)}
                      sx={{ cursor: 'pointer', color: 'error.main', bgcolor: (t) => alpha(t.palette.error.main, 0.1),
                        borderRadius: 1, px: 0.3, textDecoration: 'underline wavy', textDecorationColor: (t) => alpha(t.palette.error.main, 0.5) }}>
                      {word}
                    </Box>
                  </Tooltip>
                );
                last = offset + word.length;
              });
              if (last < piece.text.length) out.push(<span key="tail">{piece.text.slice(last)}</span>);
              return <React.Fragment key={k}>{out}</React.Fragment>;
            })}
          </Typography>
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            size="small" fullWidth multiline minRows={1} placeholder="English for this sentence"
            value={sentence.en || ''} onChange={(e) => onEnglish(e.target.value)}
            error={!sentence.en}
            sx={{ '& textarea': { fontSize: '0.9rem' } }}
          />
        </Grid>
      </Grid>
      {editor}
    </Box>
  );
}

function UnitEditor({ unit, canJoin, onChange, onJoin, onSplit, onRemove, onClose }) {
  if (!unit) return null;
  return (
    <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, minWidth: 120 }}>{unit.t}</Typography>
        <TextField size="small" label="Meaning here" value={unit.g || ''} onChange={(e) => onChange('g', e.target.value)}
          autoFocus sx={{ flexGrow: 1 }} />
        <TextField size="small" label="Dictionary form (optional)" value={unit.l || ''} onChange={(e) => onChange('l', e.target.value)}
          sx={{ minWidth: 200 }} />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
        <Button size="small" disabled={!canJoin} onClick={onJoin} sx={{ textTransform: 'none' }}>Join with next word</Button>
        <Button size="small" disabled={!/\s/.test(unit.t)} onClick={onSplit} sx={{ textTransform: 'none' }}>Split into words</Button>
        <Button size="small" color="error" onClick={onRemove} sx={{ textTransform: 'none' }}>Remove</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none' }}>Done</Button>
      </Stack>
    </Paper>
  );
}
